import React, { useState } from 'react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Copy, Loader2, GitMerge, CheckCircle2 } from 'lucide-react';
import { formatDate } from '@/lib/utils';

const REASONS = {
  email: ['Gleiche E-Mail', 'bg-blue-100 text-blue-700'],
  swapped_names: ['Vertauschte Namen', 'bg-amber-100 text-amber-700'],
  name_dob: ['Gleicher Name + Geburtsdatum', 'bg-brand-red/10 text-brand-red'],
};

const fmtDate = (d) => (d ? formatDate(d) || '–' : '–');

export const DuplicateCheckDialog = ({ onMerged }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState(null);
  const [studentGroups, setStudentGroups] = useState(null);
  const [keep, setKeep] = useState({});
  const [keepStudent, setKeepStudent] = useState({});
  const [merging, setMerging] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [g, s] = await Promise.all([
        api.get('/guardians/duplicates'),
        api.get('/students/duplicates'),
      ]);
      setGroups(g.data.groups);
      setStudentGroups(s.data.groups);
      setKeep({});
      setKeepStudent({});
    } finally { setLoading(false); }
  };

  const openDialog = () => { setOpen(true); load(); };

  const merge = async (gi, group) => {
    const keepId = keep[gi];
    const mergeIds = group.guardians.filter((g) => g.id !== keepId).map((g) => g.id);
    setMerging(gi);
    try {
      for (const mid of mergeIds) {
        await api.post('/guardians/merge', { keep_id: keepId, merge_id: mid });
      }
      toast.success('Dublette zusammengeführt und gelöscht');
      await load();
      onMerged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Zusammenführen fehlgeschlagen');
    } finally { setMerging(null); }
  };

  const mergeStudents = async (gi, group) => {
    const keepId = keepStudent[gi];
    const mergeIds = group.students.filter((s) => s.id !== keepId).map((s) => s.id);
    setMerging(`s-${gi}`);
    try {
      for (const mid of mergeIds) {
        await api.post('/students/merge', { keep_id: keepId, merge_id: mid });
      }
      toast.success('Schüler-Dublette zusammengeführt und gelöscht');
      await load();
      onMerged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Zusammenführen fehlgeschlagen');
    } finally { setMerging(null); }
  };

  return (
    <>
      <Button variant="outline" onClick={openDialog} data-testid="duplicate-check-btn">
        <Copy className="h-4 w-4 mr-2" /> Dubletten-Prüfung
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dubletten-Prüfung</DialogTitle>
            <DialogDescription>
              Mögliche Dubletten bei Zahlungspflichtigen (gleiche E-Mail oder vertauschte Namen) und Schülern (gleicher Name + Geburtsdatum).
              Wähle je Gruppe den korrekten Datensatz – fehlende Daten und Verknüpfungen werden übernommen, das Duplikat wird gelöscht.
            </DialogDescription>
          </DialogHeader>

          {loading && <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin inline text-slate-400" /></div>}

          {!loading && groups && groups.length === 0 && (!studentGroups || studentGroups.length === 0) && (
            <div className="py-8 text-center text-emerald-600 text-sm flex items-center justify-center gap-2" data-testid="duplicate-none">
              <CheckCircle2 className="h-5 w-5" /> Keine Dubletten gefunden
            </div>
          )}

          {!loading && groups?.length > 0 && (
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Zahlungspflichtige</div>
          )}

          {!loading && groups?.map((group, gi) => {
            const [lbl, cls] = REASONS[group.reason] || [group.reason, 'bg-slate-100 text-slate-600'];
            return (
              <div key={gi} className="border border-slate-200 rounded-lg p-4 mb-3" data-testid={`duplicate-group-${gi}`}>
                <div className="flex items-center justify-between mb-3">
                  <Badge className={`${cls} border-0`}>{lbl}</Badge>
                  <Button
                    size="sm"
                    disabled={!keep[gi] || merging === gi}
                    onClick={() => merge(gi, group)}
                    data-testid={`duplicate-merge-btn-${gi}`}
                  >
                    {merging === gi ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <GitMerge className="h-4 w-4 mr-1" />}
                    Zusammenführen
                  </Button>
                </div>
                <div className="space-y-2">
                  {group.guardians.map((g) => (
                    <label
                      key={g.id}
                      className={`flex items-start gap-3 p-2.5 rounded border cursor-pointer transition-colors ${
                        keep[gi] === g.id ? 'border-brand-red/40 bg-brand-red/5' : 'border-slate-200 hover:bg-slate-50'
                      }`}
                      data-testid={`duplicate-option-${g.id}`}
                    >
                      <input
                        type="radio"
                        name={`keep-${gi}`}
                        checked={keep[gi] === g.id}
                        onChange={() => setKeep({ ...keep, [gi]: g.id })}
                        className="mt-1"
                      />
                      <div className="text-xs space-y-0.5 min-w-0">
                        <div className="text-sm font-semibold text-slate-900">
                          {g.last_name}, {g.first_name}
                          {keep[gi] === g.id && <span className="ml-2 text-brand-red font-normal">← wird behalten</span>}
                        </div>
                        <div className="text-slate-500">{g.email || 'keine E-Mail'}{g.phone ? ` · ${g.phone}` : ''}</div>
                        <div className="text-slate-500">
                          IBAN: <span className="font-mono">{g.bank_iban || '–'}</span>
                          {' · '}Mandat: {g.sepa_mandate_reference || '–'}
                          {g.sepa_mandate_date ? ` (${fmtDate(g.sepa_mandate_date)})` : ''}
                        </div>
                        <div className="text-slate-500">Schüler: {g.student_names?.length ? g.student_names.join(', ') : '–'}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}

          {!loading && studentGroups?.length > 0 && (
            <>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 mt-2">Schüler</div>
              {studentGroups.map((group, gi) => {
                const [lbl, cls] = REASONS[group.reason] || [group.reason, 'bg-slate-100 text-slate-600'];
                return (
                  <div key={`s-${gi}`} className="border border-slate-200 rounded-lg p-4 mb-3" data-testid={`duplicate-student-group-${gi}`}>
                    <div className="flex items-center justify-between mb-3">
                      <Badge className={`${cls} border-0`}>{lbl}</Badge>
                      <Button
                        size="sm"
                        disabled={!keepStudent[gi] || merging === `s-${gi}`}
                        onClick={() => mergeStudents(gi, group)}
                        data-testid={`duplicate-student-merge-btn-${gi}`}
                      >
                        {merging === `s-${gi}` ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <GitMerge className="h-4 w-4 mr-1" />}
                        Zusammenführen
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {group.students.map((s) => (
                        <label
                          key={s.id}
                          className={`flex items-start gap-3 p-2.5 rounded border cursor-pointer transition-colors ${
                            keepStudent[gi] === s.id ? 'border-brand-red/40 bg-brand-red/5' : 'border-slate-200 hover:bg-slate-50'
                          }`}
                          data-testid={`duplicate-student-option-${s.id}`}
                        >
                          <input
                            type="radio"
                            name={`keep-student-${gi}`}
                            checked={keepStudent[gi] === s.id}
                            onChange={() => setKeepStudent({ ...keepStudent, [gi]: s.id })}
                            className="mt-1"
                          />
                          <div className="text-xs space-y-0.5 min-w-0">
                            <div className="text-sm font-semibold text-slate-900">
                              {s.last_name}, {s.first_name}
                              {keepStudent[gi] === s.id && <span className="ml-2 text-brand-red font-normal">← wird behalten</span>}
                            </div>
                            <div className="text-slate-500">
                              Geb.: {fmtDate(s.date_of_birth)} · Status: {s.status || '–'} · Belegungen: {s.enrollment_count}
                            </div>
                            <div className="text-slate-500">{s.email || 'keine E-Mail'}{s.phone ? ` · ${s.phone}` : ''}</div>
                            <div className="text-slate-500">Erziehungsberechtigte: {s.guardian_names?.length ? s.guardian_names.join(', ') : '–'}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
